/**
 * monetization/ads-policy — **CashSync** advertising POLICY (tier A).
 *
 * This app is **tier A** in `infra/guides/ADS_STRATEGY.md` §9 (consumer, full
 * ads: rewarded + native + interstitial-at-breakpoint), and its `free` tier is
 * ad-supported (`adsEnabled: true` in the kit defaults). But this is a MONEY app,
 * and §10 (row 6) plus the cross-cutting notes in that table impose two
 * restrictions that are NOT optional or merely legal — they are product rules:
 *
 *   1. **Contextual ads, NEVER behavioral targeting over financial data.** Not
 *      one signal derived from the user's content (amounts, balances, categories,
 *      payees, currencies, debts, budgets) may leave the device toward an ad
 *      network or be used for targeting. The permitted context is the SCREEN,
 *      not the money on it.
 *   2. **Never next to editable amounts.** No ad is rendered on a screen where
 *      the user is entering or correcting an amount. This is §1's non-intrusion
 *      rule taken to the extreme required by the domain: a banner beside the
 *      "importe" field is a tap error with accounting consequences.
 *
 * **Status**: the real placements already exist (`growth/domain.ts`:
 * `month-close`, `monthly-stats`, `streak-save`, `theme-trial`, `export-boost`)
 * and **all pass through here**: `evaluateCashsyncPlacement` translates each one
 * into an `AdRequest`, and `assertCashsyncAdPolicy()` guards against any slipping
 * through. The `AdsProvider` consults this layer again on every `gate()`, with the
 * `amountBeingEdited` flag live from the amount editors.
 *
 * It is pure TypeScript (zero dependencies, zero SDK). The only backend capable
 * of loading a script lives in `growth/provider.ts` and is instantiated only when
 * ads are enabled — which they are not by default.
 */

import type { AdContentRating, AdKind } from "../growth/types";
import { AD_CONTENT_RATINGS } from "../growth/types";

/**
 * Formats allowed in CashSync. Tier A **without banner**: a persistent banner
 * would coexist with transaction lists and amounts, exactly what the policy
 * forbids. The other three live at breathing points (§3).
 */
export const ALLOWED_AD_KINDS: readonly AdKind[] = ["rewarded", "native", "interstitial"];

/**
 * Surfaces where an ad can NEVER appear, because there is an amount the user is
 * editing (or about to confirm). This is not a list of routes but of CONTEXTS:
 * each placement integration must declare its own.
 */
export const FORBIDDEN_AD_SURFACES = [
  "tx-editor", // add/edit transaction (amount field)
  "transfer-editor", // transfer between wallets
  "budget-editor", // set a budget amount
  "debt-editor", // debt and debt payments
  "wallet-editor", // initial balance of a wallet
  "balance-adjustment", // balance adjustment
] as const;
export type ForbiddenAdSurface = (typeof FORBIDDEN_AD_SURFACES)[number];

/**
 * Targeting signals that can NEVER cross the device boundary or reach an ad
 * network. The list is explicit on purpose: a policy written in prose cannot be
 * tested, and "do not be behavioral" is too vague to stop someone adding
 * `category` to `targeting` "just to improve relevance".
 */
export const FORBIDDEN_TARGETING_SIGNALS = [
  "amount",
  "base_amount",
  "fee_amount",
  "to_amount",
  "balance",
  "initial_balance",
  "currency",
  "base_currency",
  "category",
  "category_id",
  "payee",
  "note",
  "wallet",
  "wallet_id",
  "budget",
  "debt",
  "exchange_rate",
  "net_worth",
  "income",
  "expense",
] as const;
export type ForbiddenTargetingSignal = (typeof FORBIDDEN_TARGETING_SIGNALS)[number];

/** Description of an ad intended for display. */
export interface AdRequest {
  kind: AdKind;
  /** Context declared by the placement (`month-close`, `stats`, `tx-editor`…). */
  surface: string;
  /**
   * Targeting keys the placement wants to send to the network. CashSync's policy
   * is that this is ALWAYS empty or contains only signals not derived from
   * financial content (language, country, platform).
   */
  targeting?: readonly string[];
  /** true while an amount is being edited on screen (the form is open). */
  amountBeingEdited?: boolean;
}

export interface AdPolicyVerdict {
  allowed: boolean;
  /** Stable reason (feeds tests and telemetry); `"ok"` if allowed. */
  reason:
    | "ok"
    | "forbidden-kind"
    | "forbidden-surface"
    | "amount-being-edited"
    | "behavioral-targeting-on-financial-data";
}

const NON_FINANCIAL_SIGNALS = new Set<string>(["locale", "country", "platform", "theme"]);

function isFinancialSignal(signal: string): boolean {
  const normalized = signal.trim().toLowerCase();
  if (NON_FINANCIAL_SIGNALS.has(normalized)) {
    return false;
  }
  // Any signal not explicitly declared safe is considered financial: by default
  // it is PROHIBITED. In a money app, the allowlist is the only safe direction
  // for failure.
  return true;
}

/**
 * Can this ad be shown under CashSync's policy? PURE function:
 * the `AdProvider` decides *whether it can* (flags/tier/platform), and this layer
 * decides *whether the domain allows it*. Both must say yes.
 */
export function evaluateAdPolicy(request: AdRequest): AdPolicyVerdict {
  if (!ALLOWED_AD_KINDS.includes(request.kind)) {
    return { allowed: false, reason: "forbidden-kind" };
  }
  if ((FORBIDDEN_AD_SURFACES as readonly string[]).includes(request.surface)) {
    return { allowed: false, reason: "forbidden-surface" };
  }
  if (request.amountBeingEdited === true) {
    return { allowed: false, reason: "amount-being-edited" };
  }
  if ((request.targeting ?? []).some((signal) => isFinancialSignal(signal))) {
    return { allowed: false, reason: "behavioral-targeting-on-financial-data" };
  }
  return { allowed: true, reason: "ok" };
}

// ── Financial DOMAIN regime floor (not negotiable by config) ────────────────

/**
 * **CashSync's regime floor**, above the tier floor.
 *
 * The ads kit declares a floor per TIER (`AD_TIER_REGIMES`): C/D without ads, K
 * child-directed, A with no special floor. CashSync is tier `A`… and by tier an
 * `A` could serve behavioral ads. This is where that is closed: the financial
 * policy above (rule 1) is one MORE floor, and `clampAdsConfigToTier` applies it
 * at the end of resolution just like the tier floor.
 *
 * Why a declaration instead of a loose `if` in the provider: the hole closed by
 * the canon (`hardenAdInitOptions`) was exactly that — the policy lived at the
 * call site, so the call site could undo it. A declared floor is applied at ONE
 * point and tested through all three paths (env, admin override and `init` options).
 */
export interface AdRegimeFloor {
  /** ONLY contextual ads: all behavioral targeting is forbidden. */
  readonly contextualOnly: boolean;
  /** Formats allowed by the domain (no banner in this app). */
  readonly allowedKinds: readonly AdKind[];
  /** Domain rating ceiling (can never be raised by configuration). */
  readonly maxAdContentRating: AdContentRating;
}

/**
 * The actual floor for this app. `contextualOnly` is rule 1 written as data:
 * no combination of env/override/`init()` can lower it.
 *
 * The rating ceiling is `T`: CashSync is not a child-directed app (it does not
 * force `G`), but a money app has no reason to accept the loosest catalog rating
 * either — and declaring it prevents an `ADS_MAX_CONTENT_RATING=MA` in a
 * production `.env` from going unnoticed.
 */
export const CASHSYNC_AD_REGIME_FLOOR: AdRegimeFloor = {
  contextualOnly: true,
  allowedKinds: ALLOWED_AD_KINDS,
  maxAdContentRating: "T",
};

/** Minimal config shape touched by the financial floor. */
export interface AdRegimeConfig {
  contextualOnly: boolean;
  maxAdContentRating: AdContentRating;
}

/**
 * Applies the financial floor to an already-resolved config. It **only tightens**:
 * turns on `contextualOnly` and lowers the rating if needed, never the reverse.
 * Idempotent (applying it twice gives the same result).
 */
export function clampAdsConfigToFinancialPolicy(config: AdRegimeConfig): void {
  if (CASHSYNC_AD_REGIME_FLOOR.contextualOnly) {
    config.contextualOnly = true;
  }
  // `AD_CONTENT_RATINGS` goes from strictest to loosest: "stricter" = lower
  // index. It is compared with the catalog, not a duplicated list here.
  const floor = CASHSYNC_AD_REGIME_FLOOR.maxAdContentRating;
  if (AD_CONTENT_RATINGS.indexOf(config.maxAdContentRating) > AD_CONTENT_RATINGS.indexOf(floor)) {
    config.maxAdContentRating = floor;
  }
}

/** Throwing variant: use as a guard in placement integrations. */
export function assertAdPolicy(request: AdRequest): void {
  const verdict = evaluateAdPolicy(request);
  if (!verdict.allowed) {
    throw new Error(
      `CashSync ads policy violation (${verdict.reason}) for ${request.kind} on "${request.surface}"`,
    );
  }
}
